import React, { useState } from 'react';
import { Spinner } from '@tag/tag-components-react-v4';

import Checkbox from '@/components/common/Checkbox/Checkbox';
import AgConfirmationModal from '@/components/common/ModalWindow/AgConfirmationModal';
import Toggle from '@/components/common/Toggle/Toggle';

import { EntityTypeEnum } from '@/graphql/contracts/_generated_/contracts';
import { MISSING_AGREEMENT_FIELD } from './contractIqVerification.config';

import styles from './contract-iq-verify-modal.module.scss';

interface VerifiableField {
  field: string;
  reasonCode: string;
  entityType: EntityTypeEnum;
}

interface IFieldCheckboxListProps {
  fields: VerifiableField[];
  selectedFields: string[];
  onToggleField: (field: string) => void;
  onToggleAll: () => void;
  onToggleMissingAgreement: () => void;
  showMissingAgreement: boolean;
  isLoading: boolean;
  isFullAccess: boolean;
  onRemoveField?: (field: VerifiableField) => void;
  isRemoving?: boolean;
}

export default function FieldCheckboxList({
  fields,
  selectedFields,
  onToggleField,
  onToggleAll,
  onToggleMissingAgreement,
  showMissingAgreement,
  isLoading,
  isFullAccess,
  onRemoveField,
  isRemoving,
}: IFieldCheckboxListProps) {
  const normalFields = fields.filter(field => field.field !== MISSING_AGREEMENT_FIELD);
  const missingAgreementSelected = selectedFields.includes(MISSING_AGREEMENT_FIELD);
  const allSelected = normalFields.length > 0
    && normalFields.every(field => selectedFields.includes(field.field));
  const [fieldToDelete, setFieldToDelete] = useState<VerifiableField | null>(null);

  if (isLoading) {
    return (
      <div className={styles['fields-loading']}>
        <Spinner />
      </div>
    );
  }

  if (normalFields.length === 0 && !showMissingAgreement) {
    return <div className={styles['fields-empty']}>No verifiable fields available for this reason code.</div>;
  }

  return (
    <>
      <div className={styles['fields-section']}>
        <div className={styles['fields-header']}>
          <div className={styles['select-all']}>
            <Checkbox
              id="contract-iq-select-all"
              text="Select All"
              isChecked={allSelected}
              disabled={missingAgreementSelected || normalFields.length === 0}
              onChange={onToggleAll}
            />
          </div>
          {showMissingAgreement && (
            <div className={styles['missing-agreement']}>
              <label htmlFor="missing-agreement-toggle">
                {MISSING_AGREEMENT_FIELD}
              </label>
              <Toggle
                id="missing-agreement-toggle"
                testId="missing-agreement-toggle"
                isEnable={missingAgreementSelected}
                onChange={onToggleMissingAgreement}
              />
            </div>
          )}
        </div>

        <div className={styles['fields-list']}>
          {normalFields.map((vf) => (
            <div key={vf.field} className={styles['field-item']}>
              <div className={styles['field-item-content']}>
                <Checkbox
                  id={`field-${vf.field}`}
                  text={vf.field}
                  isChecked={selectedFields.includes(vf.field)}
                  disabled={missingAgreementSelected}
                  onChange={() => onToggleField(vf.field)}
                />
                {isFullAccess && onRemoveField && (
                  <button
                    type="button"
                    className={styles['remove-field-btn']}
                    onClick={(e) => {
                      e.stopPropagation();
                      setFieldToDelete(vf);
                    }}
                    title={`Remove ${vf.field}`}
                    aria-label={`Remove ${vf.field}`}
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {fieldToDelete && (
        <AgConfirmationModal
          isOpen={true}
          onClose={() => setFieldToDelete(null)}
          onConfirm={() => {
            onRemoveField?.(fieldToDelete);
            setFieldToDelete(null);
          }}
          title="Remove Verifiable Field"
          confirmText={isRemoving ? 'Removing...' : 'Remove'}
          isConfirmButtonDisabled={isRemoving}
          contentStyle={{ width: 420 }}
        >
          <span>Are you sure you want to remove <strong>{fieldToDelete.field}</strong>?</span>
          <br />
          <span>This action cannot be undone.</span>
        </AgConfirmationModal>
      )}
    </>
  );
}
